/**
 * TenantFilter — sidebar-mounted control that lets an admin who operates
 * across tenants (super-admin, or any platform role) scope the shared admin
 * pages to one tenant at a time.
 *
 * Selection persists in localStorage; pages read the current value via
 * `useSelectedTenantId()` (or `getSelectedTenantId()` for non-React code).
 * Pages don't actively consume the value yet — the dropdown is the visible
 * scaffolding for the multi-tenancy rollout (see
 * docs/FeatureDesigns/multi-tenancy/multi-tenancy-rollout-plan.md §T1 step 3, T2/T3).
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Select } from '@shared-ui/components';
import { tenantsApi, type TenantNameRow } from '../api/tenantsApi';
import { SELECTED_TENANT_KEY as STORAGE_KEY } from '../api/auth';
import './TenantFilter.css';

/** Sentinel meaning "all tenants — don't scope". */
export const ALL_TENANTS = '__all__';
/** System/platform tenant — holds platform-level admin records, not
 *  per-tenant data. Treated like ALL_TENANTS for tenant-scoped pages. */
export const PLATFORM_TENANT = 'platform';

/**
 * True only when a real tenant (not ALL_TENANTS, not the system 'platform'
 * tenant) is currently selected. Pages that need a tenant context to render
 * should gate on this.
 */
export function hasScopedTenant(id: string): boolean {
    return id !== ALL_TENANTS && id !== PLATFORM_TENANT;
}

const subscribers = new Set<(id: string) => void>();

export function getSelectedTenantId(): string {
    return localStorage.getItem(STORAGE_KEY) ?? ALL_TENANTS;
}

function setSelectedTenantId(id: string): void {
    if (id === ALL_TENANTS) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, id);
    subscribers.forEach(fn => fn(id));
}

export function useSelectedTenantId(): string {
    const [value, setValue] = useState<string>(getSelectedTenantId);
    useEffect(() => {
        subscribers.add(setValue);
        return () => { subscribers.delete(setValue); };
    }, []);
    return value;
}

export function TenantFilter() {
    const selected = useSelectedTenantId();
    // Names only: the filter needs id + name, and that endpoint is open to the
    // platform tier — the full list is a super-admin management surface.
    const { data, isError } = useQuery({
        queryKey: ['platform-tenant-names-for-filter'],
        queryFn: () => tenantsApi.names(),
        staleTime: 60_000,
    });
    const tenants: TenantNameRow[] = data?.tenants ?? [];

    // A stored id the list does not carry — a tenant since deleted, a value
    // left by another admin in this browser, or a list that failed to load —
    // would be sent to every page while the <select> shows "All tenants".
    // Reset it, so what the control shows is what the pages ask for.
    const settled = isError || data !== undefined;
    const known = selected === ALL_TENANTS || tenants.some(t => t.id === selected);
    useEffect(() => {
        if (settled && !known) setSelectedTenantId(ALL_TENANTS);
    }, [settled, known]);

    return (
        <label className="tenant-filter">
            <span className="tenant-filter-label">Tenant</span>
            <Select
                value={selected}
                onChange={e => setSelectedTenantId(e.target.value)}
            >
                <option value={ALL_TENANTS}>All tenants</option>
                {tenants.map(t => (
                    <option key={t.id} value={t.id}>
                        {t.name || t.id}
                    </option>
                ))}
            </Select>
        </label>
    );
}
